import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../../lib/api.js'
import {
  Card, CopyButton, CopyRow, Empty, ErrorBox, Page, Skeleton, useAsync, useToast
} from '../../components/Ui.jsx'
import Icon from '../../components/Icon.jsx'
import { useAuth } from '../../lib/auth.jsx'
import {
  MESSAGES, publicDirectoryUrl, publicHatcheryUrl, smsUrl, whatsappUrl
} from '../../lib/share.js'

/**
 * The link the desk sends farmers. One screen for: copy it, send it, see what
 * the farmer will see, and watch the leads it brings back.
 */
export default function Share () {
  const toast = useToast()
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  const directory = publicDirectoryUrl()

  const [lang, setLang] = useState('en')
  const [phone, setPhone] = useState('')
  const [draft, setDraft] = useState(() => MESSAGES[0].body(publicDirectoryUrl()))

  // Read the directory itself rather than the admin tables: it is exactly what
  // the farmer will see, and an operator can open this screen too.
  const directoryData = useAsync(
    () => api.get('/public/hatcheries'), [],
    { live: ['hatcheries', 'batches', 'settings'] }
  )
  const leads = useAsync(() => api.get('/operator/enquiries'), [], { live: ['enquiries'] })

  const pickLang = key => {
    const tpl = MESSAGES.find(m => m.key === key) || MESSAGES[0]
    setLang(key)
    setDraft(tpl.body(directory))
  }

  const showsPrices = Boolean(directoryData.data?.showPrices)
  const live = useMemo(() => directoryData.data?.hatcheries || [], [directoryData.data])
  const withSeed = live.filter(h => h.liveBatches > 0)

  const openWhatsapp = () => {
    window.open(whatsappUrl(draft, phone), '_blank', 'noopener')
    toast?.info('WhatsApp opened with the message ready to send')
  }

  return (
    <Page
      title="Farmer link"
      subtitle="Send this one link to a farmer over WhatsApp. They open it, browse the hatcheries with seed ready, and tap “Request seed” — the request lands on the operator desk as an enquiry. Farmers never book directly and never see hatchery phone numbers."
      actions={
        <>
          <CopyButton className="btn primary" value={directory} told="Farmer link copied">Copy link</CopyButton>
          <a className="btn" href={directory} target="_blank" rel="noreferrer">Open as a farmer sees it</a>
        </>
      }
    >
      <div className="split">
        <div>
          <Card title="The link">
            <CopyRow
              label="Public hatchery directory"
              value={directory}
              wrap
              hint="Works on any phone, no sign-in, no app. Safe to forward inside farmer WhatsApp groups."
            />

            <div className="sharefacts">
              <div>
                <b>{live.length}</b>
                <span>hatcheries listed</span>
              </div>
              <div>
                <b>{withSeed.length}</b>
                <span>showing seed right now</span>
              </div>
              <div>
                <b>{leads.data?.counts?.new ?? '—'}</b>
                <span>new enquiries waiting</span>
              </div>
              <div>
                <b>{leads.data?.counts?.converted ?? '—'}</b>
                <span>turned into bookings</span>
              </div>
            </div>

            <div className={`alert ${showsPrices ? 'warn' : 'info'}`} style={{ marginTop: 16 }}>
              <div>
                <b>Farmers on this link {showsPrices ? 'can see prices' : 'cannot see prices'}</b>
                {showsPrices
                  ? 'Rates and offers are visible in the directory. Turn this off if the desk wants to quote on the phone.'
                  : 'Rates and offers stay hidden — the desk quotes on the phone. Hatchery phone numbers are always hidden.'}
                {isAdmin && (
                  <div className="btnrow" style={{ marginTop: 8 }}>
                    <Link className="btn sm" to="/admin/settings">Change in Settings</Link>
                  </div>
                )}
              </div>
            </div>
          </Card>

          <Card title="Message to send">
            <div className="tabs">
              {MESSAGES.map(m => (
                <button key={m.key} type="button" className={`tab ${lang === m.key ? 'on' : ''}`}
                  onClick={() => pickLang(m.key)}>
                  {m.label}
                </button>
              ))}
            </div>

            <textarea className="sharedraft" rows={11} value={draft} onChange={e => setDraft(e.target.value)} />

            <div className="formgrid two" style={{ marginTop: 12 }}>
              <label className="f">
                <span>Farmer's WhatsApp number <span className="muted">(optional)</span></span>
                <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+91 98765 43210" />
                <div className="hint">Leave blank to pick the contact inside WhatsApp.</div>
              </label>
            </div>

            <div className="btnrow">
              <button type="button" className="btn primary" onClick={openWhatsapp}>
                <Icon name="link" size={16} /> Send on WhatsApp
              </button>
              <a className="btn" href={smsUrl(draft, phone)}>Send as SMS</a>
              <CopyButton className="btn" value={draft} told="Message copied">Copy message</CopyButton>
            </div>
          </Card>
        </div>

        <div>
          <Card title="What the farmer does" className="flow-card">
            <ol className="flowsteps">
              <li><b>Opens the link</b><span>No sign-in. Hatcheries with seed ready, sorted by distance from the belt.</span></li>
              <li><b>Picks a hatchery</b><span>Sees stage, ready date, PCR status and tank photos — never a phone number.</span></li>
              <li><b>Taps “Request seed”</b><span>Name, village, pond size and the stocking date they are aiming for.</span></li>
              <li><b>Desk rings back</b><span>The enquiry appears on the operator desk instantly and the operator books it.</span></li>
            </ol>
            <div className="btnrow">
              <Link className="btn sm" to="/operator/enquiries">Open enquiries</Link>
            </div>
          </Card>

          <Card title="Link for one hatchery" padded={false}
            actions={isAdmin ? <Link className="btn sm" to="/admin/hatcheries">Manage</Link> : null}>
            {directoryData.loading && <div className="body"><Skeleton rows={4} /></div>}
            {directoryData.error && <div className="body"><ErrorBox error={directoryData.error} onRetry={directoryData.reload} /></div>}
            {directoryData.data && (
              <div className="scroll-x">
                <table>
                  <tbody>
                    {live.map(h => (
                      <tr key={h.id}>
                        <td>
                          <b>{h.name}</b>
                          <span className="t-sub">{h.place} · {h.liveBatches} live {h.liveBatches === 1 ? 'batch' : 'batches'}</span>
                        </td>
                        <td className="right nowrap">
                          <CopyButton value={publicHatcheryUrl(h.id)} told={`Link to ${h.name} copied`} />{' '}
                          <a className="btn sm" href={publicHatcheryUrl(h.id)} target="_blank" rel="noreferrer">Open</a>
                        </td>
                      </tr>
                    ))}
                    {!live.length && <tr><td><Empty>No active hatcheries to share yet.</Empty></td></tr>}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      </div>
    </Page>
  )
}
